import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Modal } from '@carbon/react';
import routes from '~/routes';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import { curvesAtom, useRefreshCurves } from 'pages/pricing/shared/atoms';
import { pricingApi } from 'pages/pricing/shared/api';
import type { CurveOption, Transformation } from 'pages/pricing/shared/types';
import { TransformationLibrary } from './transformation-library';
import { TransformationEditor } from './transformation-editor';

type Toast = { type: 'success' | 'error'; message: string; subtitle?: string };

export function TransformationBuilderContent() {
  const curves = useAtomValue(curvesAtom);
  const refreshCurves = useRefreshCurves();
  const [transformations, setTransformations] = useState<Transformation[]>([]);
  // Selection lives in the URL: /pricing/transformation-builder (list) vs .../:id (detail),
  // so detail views are deep-linkable and the browser Back button pops detail → list.
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  // `/new` is an UNSAVED draft: the editor renders but nothing is persisted (and so nothing shows
  // in the list) until the first Save. A real id means an existing, persisted transformation.
  const isNew = id === 'new';
  const activeId = id && id !== 'new' ? Number(id) : null;
  const NEW_PATH = routes.pricingTransformationDetailPath.replace(':id', 'new');
  const [loaded, setLoaded] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const refresh = useCallback(async () => {
    const [t] = await Promise.all([
      pricingApi.listTransformations(),
      refreshCurves(),
    ]);
    setTransformations(t.results);
    return t.results;
  }, [refreshCurves]);

  useEffect(() => {
    refresh()
      .catch((e: Error) => setToast({ type: 'error', message: e.message }))
      .finally(() => setLoaded(true));
  }, [refresh]);

  const curveOptions: CurveOption[] = useMemo(
    () => curves.map((c) => ({ value: c.id, label: c.name })),
    [curves]
  );

  const active = transformations.find((t) => t.id === activeId) ?? null;
  const pendingDelete = transformations.find((t) => t.id === deleteId) ?? null;

  // A blank, UNSAVED draft rendered for the `/new` route. Not persisted until the first Save, so
  // it never appears in the list. The default name is a placeholder the editor blocks Save on
  // (the user must rename it first).
  const draft = useMemo<Transformation>(
    () => ({
      id: 0,
      href: '',
      auth: [],
      createdAt: '',
      updatedAt: '',
      name: `New transformation ${transformations.length + 1}`,
      commodity: 'crude',
      benchmark: null,
      status: 'draft',
      createdBy: null,
      updatedBy: null,
      steps: [],
    }),
    [transformations.length]
  );
  const editorTx = isNew ? draft : active;

  const open = useCallback(
    (tid: number) =>
      history.push(
        routes.pricingTransformationDetailPath.replace(':id', String(tid))
      ),
    [history]
  );
  const backToList = useCallback(
    () => history.push(routes.pricingTransformationBuilderPath),
    [history]
  );

  // Deep-link / stale-link guard: an id that resolves to no transformation (never existed,
  // or was just deleted) bounces back to the list once data has loaded.
  useEffect(() => {
    if (loaded && activeId !== null && !active) backToList();
  }, [loaded, activeId, active, backToList]);

  // "Add" opens a blank draft at `/new` WITHOUT persisting — the record is created on first Save,
  // so it stays out of the list until then.
  const addTransformation = () => history.push(NEW_PATH);

  const duplicate = async () => {
    if (!active) return;
    try {
      const saved = await pricingApi.createTransformation({
        name: `${active.name} (copy)`,
        commodity: active.commodity,
        benchmark: active.benchmark,
        status: 'draft',
        steps: active.steps.map((s) => ({ ...s })),
      });
      await refresh();
      open(saved.id);
      setToast({
        type: 'success',
        message: 'Transformation cloned',
        subtitle: saved.name,
      });
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
    }
  };

  // Save creates the record on a `/new` draft (then navigates to its real id, adding it to the
  // list), or updates the existing one.
  const save = async (
    payload: Partial<Transformation>
  ): Promise<Transformation> => {
    try {
      const saved = isNew
        ? await pricingApi.createTransformation({
            name: (payload.name ?? '').trim(),
            commodity: 'crude',
            status: 'draft',
            steps: payload.steps ?? [],
          })
        : await pricingApi.updateTransformation(activeId as number, payload);
      await refresh();
      if (isNew) open(saved.id);
      setToast({
        type: 'success',
        message: isNew ? 'Transformation created' : 'Changes saved',
        subtitle: saved.name,
      });
      return saved;
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
      throw e;
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const name = transformations.find((t) => t.id === deleteId)?.name ?? '';
    try {
      await pricingApi.deleteTransformation(deleteId);
      await refresh();
      if (activeId === deleteId) backToList();
      setToast({
        type: 'success',
        message: 'Transformation deleted',
        subtitle: name,
      });
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <>
      {activeId === null && !isNew && (
        <TransformationLibrary
          transformations={transformations}
          onOpen={open}
          onAdd={addTransformation}
        />
      )}

      {editorTx && (
        <TransformationEditor
          key={isNew ? 'new' : editorTx.id}
          transformation={editorTx}
          isNew={isNew}
          curveOptions={curveOptions}
          onSave={save}
          onDuplicate={duplicate}
          onRequestDelete={() => active && setDeleteId(active.id)}
        />
      )}

      <Modal
        open={Boolean(pendingDelete)}
        danger
        size="sm"
        modalHeading={pendingDelete ? `Delete “${pendingDelete.name}”?` : ''}
        modalLabel="Price Transformations"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteId(null)}
        onRequestSubmit={confirmDelete}
      >
        This cannot be undone. The transformation and all of its operations will
        be permanently removed.
      </Modal>

      {toast?.type === 'success' && (
        <SuccessToast
          message={toast.message}
          subtitle={toast.subtitle}
          onClose={() => {
            setToast(null);
            return true;
          }}
        />
      )}
      {toast?.type === 'error' && (
        <ErrorToast
          message={toast.message}
          subtitle={toast.subtitle}
          onClose={() => {
            setToast(null);
            return true;
          }}
        />
      )}
    </>
  );
}
